import { useEffect, useState } from "react";
import type { GuidedStartMethod } from "../../studio-shared/types/guided-new-project.js";
import { GUIDED_PROJECT_TYPES } from "../../studio-shared/types/guided-new-project.js";
import {
  GUIDED_STUDIO_DEMO_PRESETS,
  type GuidedStudioDemoPreset,
} from "../../studio-shared/types/guided-demo-presets.js";
import { GuidedButton, GuidedFlowCopy } from "../primitives/GuidedPrimitives.js";

export function GuidedNewProjectView({
  initialStartMethod,
  busy = false,
  validationIssues = [],
  onSubmit,
}: {
  readonly initialStartMethod?: GuidedStartMethod | null;
  readonly busy?: boolean;
  readonly validationIssues?: readonly string[];
  readonly onSubmit: (input: {
    businessName: string;
    contact: string;
    projectType: string;
    startMethod: GuidedStartMethod;
    demoPreset?: GuidedStudioDemoPreset | null;
  }) => void;
}) {
  const [businessName, setBusinessName] = useState("");
  const [contact, setContact] = useState("");
  const [projectType, setProjectType] = useState<string>(GUIDED_PROJECT_TYPES[0]);
  const [startMethod, setStartMethod] = useState<GuidedStartMethod>(initialStartMethod ?? "agency");
  const [activePresetId, setActivePresetId] = useState<string | null>(null);

  useEffect(() => {
    if (initialStartMethod) setStartMethod(initialStartMethod);
  }, [initialStartMethod]);

  const activePreset = GUIDED_STUDIO_DEMO_PRESETS.find((preset) => preset.id === activePresetId) ?? null;

  function applyPreset(preset: GuidedStudioDemoPreset) {
    setActivePresetId(preset.id);
    setBusinessName(preset.businessName);
    setContact(preset.primaryEmail || preset.primaryPhone);
    setProjectType(preset.projectType);
  }

  const canSubmit = businessName.trim().length > 0 && contact.trim().length > 0 && !busy;

  return (
    <section className="enrein-guided-view is-active" id="view-new" aria-label="New project">
      <GuidedFlowCopy title="New project" kicker="Start clean">
        <p className="enrein-guided-muted">Four fields to start — or tap a demo client to fill everything for testing.</p>
      </GuidedFlowCopy>

      <div className="enrein-guided-demo-presets" aria-label="Demo client presets">
        <p className="enrein-guided-kicker">Quick fill (testing)</p>
        <div className="enrein-guided-demo-preset-row">
          {GUIDED_STUDIO_DEMO_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`enrein-guided-demo-preset${activePresetId === preset.id ? " is-active" : ""}`}
              disabled={busy}
              onClick={() => applyPreset(preset)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="enrein-guided-form-panel">
        <label className="enrein-guided-field">
          <span>Client / business name</span>
          <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} disabled={busy} />
        </label>
        <label className="enrein-guided-field">
          <span>Email or phone</span>
          <input value={contact} onChange={(e) => setContact(e.target.value)} disabled={busy} placeholder="hello@client.com" />
        </label>
        {activePreset ? (
          <p className="enrein-guided-muted enrein-guided-preset-hint">
            Preset loaded: {activePreset.primaryPhone} · {activePreset.primaryEmail} · {activePreset.services.length} services
          </p>
        ) : null}
        <label className="enrein-guided-field">
          <span>Project type</span>
          <select value={projectType} onChange={(e) => setProjectType(e.target.value)} disabled={busy}>
            {GUIDED_PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="enrein-guided-start-method">
          <legend>Start method</legend>
          <label>
            <input
              type="radio"
              name="startMethod"
              checked={startMethod === "agency"}
              onChange={() => setStartMethod("agency")}
              disabled={busy}
            />
            I&apos;ll enter known facts
          </label>
          <label>
            <input
              type="radio"
              name="startMethod"
              checked={startMethod === "intake"}
              onChange={() => setStartMethod("intake")}
              disabled={busy}
            />
            Send client link
          </label>
        </fieldset>

        {validationIssues.length > 0 ? (
          <ul className="enrein-guided-validation">
            {validationIssues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        ) : null}

        <GuidedButton
          variant="gold"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              businessName,
              contact,
              projectType,
              startMethod,
              demoPreset: activePreset,
            })
          }
        >
          {busy ? "Creating…" : startMethod === "intake" ? "Create project and copy client link" : "Create project"}
        </GuidedButton>
      </div>
    </section>
  );
}
